import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useDuplicates } from "../api/hooks";
import { ROLE_LABEL, useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { Icon, Logo, useToast } from "./ui";

const SPACES = [
  { to: "/master-data", label: "Master Data", perm: "data.read", n: "01" },
  { to: "/manager", label: "Manager", perm: "mission.write", n: "02" },
  { to: "/prospection", label: "Prospection", perm: "prospection.execute", n: "03" },
  { to: "/resultats", label: "Résultats", perm: "results.read", n: "04" },
];

export function Shell() {
  const me = useAuth();
  const [theme, cycleTheme] = useTheme();
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const logout = async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      /* session déjà expirée : on nettoie quand même */
    }
    qc.clear();
    qc.setQueryData(["me"], null);
    nav("/login", { replace: true });
    toast("Déconnecté");
  };
  const themeLabel = theme === "auto" ? "Thème : automatique" : theme === "light" ? "Thème : clair" : "Thème : sombre";
  return (
    <div className="app">
      <header className="topbar">
        <NavLink to="/" className="brand" aria-label="Accueil"><Logo /><span>Prospection CRM</span></NavLink>
        <nav className="spaces" aria-label="Espaces">
          {SPACES.filter((s) => me.can(s.perm)).map((s) => (
            <NavLink key={s.to} to={s.to} className={({ isActive }) => (isActive ? "active" : "")}>
              <span className="n">{s.n}</span>{s.label}
            </NavLink>
          ))}
        </nav>
        <div className="spacer" />
        {me.can("users.read") && (
          <NavLink to="/admin/users" className={({ isActive }) => `btn ghost sm${isActive ? " active" : ""}`} title="Utilisateurs" aria-label="Utilisateurs">
            <Icon name="users" /><span className="hide-sm">Utilisateurs</span>
          </NavLink>
        )}
        <div className="who">
          <b>{me.operator_code ?? ROLE_LABEL[me.role ?? ""] ?? me.role}</b>
          <span className="muted">{ROLE_LABEL[me.role ?? ""] ?? ""}</span>
        </div>
        <button className="btn icon" type="button" onClick={cycleTheme} title={themeLabel} aria-label={themeLabel}><Icon name="theme" /></button>
        <button className="btn icon" type="button" onClick={logout} title="Se déconnecter" aria-label="Se déconnecter"><Icon name="logout" /></button>
      </header>
      <Outlet />
    </div>
  );
}

export function MasterDataLayout() {
  const me = useAuth();
  const dups = useDuplicates();
  const n = dups.data?.length ?? 0;
  const tabs = [
    { to: "/master-data", label: "Recherche & segmentation", end: true, show: true },
    { to: "/master-data/imports", label: "Imports", show: me.can("data.import") },
    { to: "/master-data/duplicates", label: <>Doublons {n > 0 && <span className="chip c-orange">{n}</span>}</>, show: true },
    { to: "/master-data/fields", label: "Champs", show: true },
  ];
  return (
    <>
      <nav className="subnav" aria-label="Master Data">
        {tabs.filter((t) => t.show).map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? "active" : "")}>{t.label}</NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}
